import React, { useState, useCallback, useEffect, useRef } from "react";
import { updateBlockInLayout } from "../utils";
import { toastError, toastSuccess } from "../../../utils/toastUtils";
import Textarea from "../../../components/FormComponents/Textarea";
import Button from "../../../components/FormComponents/Button";
import BuilderModal from "../../../Layouts/Modal/BuilderModal";
import BuilderTabs from "../../../Layouts/BuilderTabs";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";

const FigmaComponent = ({ setAiConfig, selectedItem }) => {
  const [figmaConfig, setFigmaConfig] = useState("");
  const [loading, setLoading] = useState(false);
  const [isValidJson, setIsValidJson] = useState(true);
  const [progress, setProgress] = useState(0);
  const textareaRef = useRef(null);

  const validateJson = (value) => {
    try {
      JSON.parse(value);
      setIsValidJson(true);
      return true;
    } catch (error) {
      setIsValidJson(false);
      return false;
    }
  };

  const handleConfigChange = (e) => {
    const value = e.target.value;
    setFigmaConfig(value);

    if (value.trim()) {
      validateJson(value);
    } else {
      setIsValidJson(true);
    }
  };

  const formatJson = () => {
    if (figmaConfig.trim() && isValidJson) {
      try {
        const parsed = JSON.parse(figmaConfig);
        const formatted = JSON.stringify(parsed, null, 2);
        setFigmaConfig(formatted);
        toastSuccess("JSON formatted successfully!");
      } catch (error) {
        toastError("Invalid JSON format");
      }
    }
  };

  const clearConfig = () => {
    setFigmaConfig("");
    setIsValidJson(true);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handlePositionScreenGenerate = async () => {
    if (!figmaConfig.trim()) {
      toastError("Please enter Figma JSON configuration");
      return;
    }

    if (!isValidJson) {
      toastError("Please fix JSON syntax errors before generating");
      return;
    }

    let progressInterval;
    try {
      setLoading(true);
      setProgress(0);

      // Phase 1: 0% to 90% over 60s (666ms per 1%)
      const startInitialProgress = () => {
        progressInterval = setInterval(() => {
          setProgress((prev) => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              startSlowProgress(); // Start next phase
              return 90;
            }
            return prev + 1;
          });
        }, 666);
      };

      // Phase 2: 90% to 100%, 1% every 10s
      const startSlowProgress = () => {
        progressInterval = setInterval(() => {
          setProgress((prev) => {
            if (prev >= 100) {
              clearInterval(progressInterval);
              return 100;
            }
            return prev + 1;
          });
        }, 10000);
      };

      startInitialProgress();

      const payload = {
        figma_nodes: JSON.parse(figmaConfig),
        section_id: selectedItem.id,
        ai_model: "gemini"
      };

      const { data } = await axiosInstance.post("/ai/figma/json", payload, {
        timeout: 120000
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (Array.isArray(data)) {
        if (data.length > 0) {
          setAiConfig({
            ...data[0],
            id: selectedItem.id
          });
          toastSuccess("Configuration generated successfully!");
        } else {
          setAiConfig("");
          console.warn("Received an empty array from /ai/figma/json");
          toastError("No configuration generated. Please try again.");
        }
      } else if (typeof data === "object" && data !== null) {
        setAiConfig({
          ...data,
          id: selectedItem.id
        });
        toastSuccess("Configuration generated successfully!");
      } else {
        setAiConfig(null);
        toastError("Invalid response format received");
      }

      setFigmaConfig("");
      setProgress(0);
    } catch (error) {
      clearInterval(progressInterval);
      handleApiError(error);
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d_flex flex_column justify_between h_100 p_3" style={{ minHeight: "200px" }}>
      {/* Header with actions */}
      <div className="d_flex justify_between items_center mb_2">
        {/*<h4 className="m_0 font_size_medium font_weight_medium">Figma JSON Configuration</h4>*/}
        <div className="d_flex gap_1">
          {(!isValidJson || figmaConfig.trim()) && (
            <Button
              size="small"
              variant="secondary"
              onClick={formatJson}
              disabled={!figmaConfig.trim() || !isValidJson}
              className="btn_sm"
            >
              Format JSON
            </Button>
          )}
          {figmaConfig.trim() && (
            <Button
              size="small"
              variant="secondary"
              onClick={clearConfig}
              disabled={!figmaConfig.trim()}
              className="btn_sm"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* JSON Input */}
      <div className="flex_1">
        <Textarea
          ref={textareaRef}
          label=""
          value={figmaConfig}
          textareaClassName={`border_radius_medium ${!isValidJson ? "border_error" : ""}`}
          onChange={handleConfigChange}
          placeholder="Paste your Figma JSON here..."
          style={{ minHeight: "150px", background: "#f9fafb" }}
        />
        {!isValidJson && <div className="text_error mt_1 text_sm">⚠️ Invalid JSON format. Please check syntax.</div>}
        {figmaConfig.trim() && isValidJson && <div className="text_success mt_1 text_sm">✅ Valid JSON format</div>}
      </div>

      {/* Progress bar */}
      {loading && (
        <div className="mt_2 mb_2">
          <div className="progress_bar_container">
            <div className="progress_bar_fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="text_center text_sm mt_1">Generating configuration... {progress}%</div>
        </div>
      )}

      {/* Generate button */}
      <Button
        onClick={handlePositionScreenGenerate}
        className="mt_2"
        disabled={loading || !figmaConfig.trim() || !isValidJson}
        variant="primary"
        style={{
          background: "#912cea",
          borderColor: "#912cea",
          fontWeight: "600"
        }}
      >
        {loading ? "Generating..." : "Generate Configuration"}
      </Button>
    </div>
  );
};

const CustomComponent = ({ setAiConfig, selectedItem }) => {
  const [customConfig, setCustomConfig] = useState({
    user_instruction: "",
    files: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setCustomConfig((prev) => ({
      ...prev,
      user_instruction: value
    }));
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file) => {
    if (!file.type.startsWith("image/")) {
      toastError("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      // 5MB limit
      toastError("File size should be less than 5MB");
      return;
    }

    setCustomConfig((prev) => ({
      ...prev,
      files: file
    }));

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);

    toastSuccess("Image uploaded successfully!");
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const removeImage = () => {
    setCustomConfig((prev) => ({
      ...prev,
      files: null
    }));
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    toastSuccess("Image removed");
  };

  const handleScreenConfigGenerate = async () => {
    if (!customConfig.user_instruction.trim()) {
      toastError("Please provide instructions for the configuration");
      return;
    }

    let progressInterval;

    setLoading(true);
    setProgress(0);

    try {
      // Phase 1: 0% to 90% over 60s (666ms per +1%)
      const startInitialProgress = () => {
        progressInterval = setInterval(() => {
          setProgress((prev) => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              startSlowProgress(); // start 90% to 100%
              return 90;
            }
            return prev + 1;
          });
        }, 666);
      };

      // Phase 2: 90% to 100%, 1% every 10 seconds
      const startSlowProgress = () => {
        progressInterval = setInterval(() => {
          setProgress((prev) => {
            if (prev >= 100) {
              clearInterval(progressInterval);
              return 100;
            }
            return prev + 1;
          });
        }, 10000);
      };

      startInitialProgress();

      const formData = new FormData();

      if (customConfig.files) {
        formData.append("files", customConfig.files);
      }

      const inputParams = {
        user_instruction: customConfig.user_instruction,
        section_data: selectedItem,
        ai_model: "gemini"
      };

      formData.append("inputParams", JSON.stringify(inputParams));

      const { data } = await axiosInstance.post("/ai/figma/image", formData, {
        timeout: 120000
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (Array.isArray(data)) {
        if (data.length > 0) {
          setAiConfig({
            ...data[0],
            id: selectedItem.id
          });
          toastSuccess("Configuration generated successfully!");
        } else {
          setAiConfig("");
          console.warn("Received an empty array from /ai/figma/image");
          toastError("No configuration generated. Please try again.");
        }
      } else if (typeof data === "object" && data !== null) {
        setAiConfig({
          ...data,
          id: selectedItem.id
        });
        toastSuccess("Configuration generated successfully!");
      } else {
        setAiConfig(null);
        toastError("Invalid response format received");
      }

      setCustomConfig({ user_instruction: "", files: null });
      setImagePreview(null);
      setProgress(0); // Reset after done
    } catch (error) {
      clearInterval(progressInterval);
      handleApiError(error);
      setProgress(0); // Reset on error
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d_flex flex_column gap_2 p_3 h_100" style={{ minHeight: "200px" }}>
      {/* Header */}
      {/*<div className="d_flex justify_between items_center">*/}
      {/*  <h4 className="m_0 font_size_medium font_weight_medium">Custom Configuration</h4>*/}
      {/*</div>*/}

      {/* Instructions */}
      <div className="flex_1">
        <Textarea
          style={{ minHeight: "150px", background: "#f9fafb" }}
          label="Instructions"
          value={customConfig.user_instruction}
          onChange={handleDescriptionChange}
          textareaClassName={"border_radius_medium"}
          placeholder="Describe what you want to create. Be specific about layout, colors, components, and functionality..."
        />
        <div className="text_sm text_muted mt_1">{customConfig.user_instruction.length}/1000 characters</div>
      </div>

      {/* Image Upload */}
      <div>
        <label className="builder_label d_flex mb_2">Reference Image</label>
        <div
          className={`image_upload_area ${dragActive ? "drag_active" : ""} ${imagePreview ? "has_image" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          style={{ background: "#f9fafb" }}
        >
          {!imagePreview ? (
            <div className="upload_placeholder">
              <div className="upload_icon">📁</div>
              <div className="upload_text">
                <p className="mb_1">Drag & drop an image here or</p>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  style={{ margin: "0 auto" }}
                >
                  Browse Files
                </Button>
              </div>
              <div className="upload_hint">Supports: JPG, PNG (Max 5MB)</div>
            </div>
          ) : (
            <div className="image_preview_container">
              <img src={imagePreview} alt="Preview" className="image_preview" />
              <div className="image_overlay">
                <Button
                  variant="secondary"
                  size="small"
                  onClick={removeImage}
                  className="remove_btn"
                  style={{ margin: "0 auto" }}
                >
                  Remove
                </Button>
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: "none" }}
          />
        </div>
      </div>

      {/* Progress bar */}
      {loading && (
        <div className="mt_2 mb_2">
          <div className="progress_bar_container">
            <div className="progress_bar_fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="text_center text_sm mt_1">Generating configuration... {progress}%</div>
        </div>
      )}

      {/* Generate button */}
      <Button
        onClick={handleScreenConfigGenerate}
        className="mt_2"
        disabled={loading || !customConfig.user_instruction.trim()}
        variant="primary"
        style={{
          background: "#912cea",
          borderColor: "#912cea",
          fontWeight: "600"
        }}
      >
        {loading ? "Generating..." : "Generate Configuration"}
      </Button>
    </div>
  );
};

const ConfigDisplay = ({ textareaValue, handleTextareaChange, handleConfirm, loading, setAiConfig }) => {
  const [isValidJson, setIsValidJson] = useState(true);
  const [jsonError, setJsonError] = useState("");

  useEffect(() => {
    try {
      if (textareaValue.trim()) {
        JSON.parse(textareaValue);
        setIsValidJson(true);
        setJsonError("");
      }
    } catch (error) {
      setIsValidJson(false);
      setJsonError(error.message);
    }
  }, [textareaValue]);

  const formatJson = () => {
    try {
      const parsed = JSON.parse(textareaValue);
      const formatted = JSON.stringify(parsed, null, 2);
      handleTextareaChange({ target: { value: formatted } });
      toastSuccess("JSON formatted successfully!");
    } catch (error) {
      toastError("Invalid JSON format");
    }
  };

  return (
    <div className="d_flex flex_column justify_between h_100 p_3" style={{ minHeight: "200px" }}>
      {/* Header */}
      <div className="d_flex justify_between items_center mb_2">
        <h4 className="m_0">Generated Configuration</h4>
        <div className="d_flex gap_1">
          <Button
            size="small"
            variant="secondary"
            onClick={formatJson}
            disabled={!textareaValue.trim() || !isValidJson}
            className="ai_btn_sm"
          >
            Format JSON
          </Button>
          <Button
            size="small"
            variant="secondary"
            onClick={() => navigator.clipboard.writeText(textareaValue)}
            disabled={!textareaValue.trim()}
            className="ai_btn_sm"
          >
            Copy
          </Button>
        </div>
      </div>

      {/* Config textarea */}
      <div className="flex_1">
        <Textarea
          style={{ minHeight: "150px", background: "#f9fafb" }}
          label="Configuration JSON"
          value={textareaValue}
          onChange={handleTextareaChange}
          textareaClassName={"border_radius_medium"}
          className={!isValidJson ? "border_error" : ""}
        />
        {!isValidJson && <div className="text_error mt_1 text_sm">⚠️ JSON Error: {jsonError}</div>}
        {textareaValue.trim() && isValidJson && (
          <div className="text_success mt_1 text_sm">✅ Valid configuration format</div>
        )}
      </div>
      <div className="d_flex ml_auto gap_2">
        <Button style={{ marginLeft: "0px" }} onClick={() => setAiConfig(null)} className="mt_2" variant="secondary">
          Clear
        </Button>
        {/* Accept button */}
        <Button
          onClick={handleConfirm}
          className="mt_2"
          disabled={!textareaValue.trim() || !isValidJson || loading}
          variant="success"
          style={{ marginLeft: "0px" }}
        >
          {loading ? "Applying..." : "Accept"}
        </Button>
      </div>
    </div>
  );
};

export const AIConfigModal = ({ isOpen, onClose, aiConfig, setAiConfig, handleConfirm, selectedItem }) => {
  const [textareaValue, setTextareaValue] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (aiConfig) {
      setTextareaValue(JSON.stringify(aiConfig, null, 2));
    } else {
      setTextareaValue("");
    }
  }, [aiConfig]);

  const handleTextareaChange = useCallback(
    (e) => {
      setTextareaValue(e.target.value);

      try {
        const parsed = JSON.parse(e.target.value);
        setAiConfig(parsed);
      } catch (error) {
        //throw error
      }
    },
    [setAiConfig]
  );

  const handleConfirmWithLoading = async () => {
    setLoading(true);
    try {
      await handleConfirm();
    } finally {
      setLoading(false);
    }
  };

  const configDisplay = (
    <ConfigDisplay
      aiConfig={aiConfig}
      textareaValue={textareaValue}
      handleTextareaChange={handleTextareaChange}
      handleConfirm={handleConfirmWithLoading}
      loading={loading}
      setAiConfig={setAiConfig}
    />
  );

  const tabs = [
    {
      label: "Design by Figma",
      content: !aiConfig ? <FigmaComponent setAiConfig={setAiConfig} selectedItem={selectedItem} /> : configDisplay
    },
    {
      label: "Design by Reference Image",
      content: !aiConfig ? <CustomComponent setAiConfig={setAiConfig} selectedItem={selectedItem} /> : configDisplay
    }
  ];

  return (
    <BuilderModal
      bodyStyle={{ minWidth: "600px", padding: "0px", paddingTop: "0px" }}
      isOpen={isOpen}
      onClose={onClose}
      headerStyle={{ background: "white", borderBottom: "none" }}
      modalName={
        <p className="d_flex justify_center align_center gap_2">
          <MaterialIcon name={"auto_awesome"} style={{ color: "#912cea" }} />
          <span className="font_weight_semibold">AI-Powered Screen Creation</span>
        </p>
      }
    >
      <BuilderTabs
        tabs={tabs}
        // contentClass="mt-4"
        tabsClass="w_fit bg_light"
        containerClass="custom-tab-ai w_100 non-sticky"
        onChange={() => {
          setAiConfig(null);
        }}
      />
    </BuilderModal>
  );
};

const useAIConfigModal = ({ layoutConfig, setLayoutConfig }) => {
  const [isAiConfigModalOpen, setIsAiConfigModalOpen] = useState(false);
  const [aiConfig, setAiConfig] = useState(null);

  const handleAIConfigConfirm = useCallback(() => {
    try {
      if (aiConfig?.type === "section" || aiConfig?.type === "column" || aiConfig?.type === "block") {
        const updatedConfig = updateBlockInLayout(layoutConfig, [aiConfig]);
        setLayoutConfig(updatedConfig);
        toastSuccess("Configuration applied successfully!");
      } else {
        toastError("Invalid config type. Must be 'section', 'column', or 'block'.");
      }
      setIsAiConfigModalOpen(false);
      setAiConfig(null);
    } catch (error) {
      toastError("Failed to parse configuration. Please ensure it's valid JSON.");
      console.error("AI Config parsing error:", error);
    }
  }, [aiConfig, layoutConfig, setLayoutConfig]);

  const openAIConfigModal = useCallback(() => {
    setIsAiConfigModalOpen(true);
  }, []);

  return {
    isAiConfigModalOpen,
    setIsAiConfigModalOpen,
    aiConfig,
    setAiConfig,
    handleAIConfigConfirm,
    openAIConfigModal
  };
};

export default useAIConfigModal;
